import WebAppInstall from "../components/WebAppInstall";
// src/pages/AuthPage.js

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { FiBriefcase as BriefcaseBusiness, FiDollarSign as Landmark, FiUsers as Users, FiArrowUpRight as ArrowUpRight, FiShield as ShieldCheck, FiCheck as Check } from "react-icons/fi";

import { auth, db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import { getSafeReturnTo } from "../utils/safeReturnTo";
import { getPostAuthRoute } from "../utils/authFlow";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { LEGAL_LINKS } from "../config/legalLinks";
import { normalizeSBSLanguage } from "../i18n";
import "./AuthPage.css";

export default function AuthPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userData, loading, signIn, refreshUserData } = useAuth();
  const invitationToken = searchParams.get("invite") || "";
  const joiningWorkspace = Boolean(invitationToken);
  const returnTo = getSafeReturnTo(searchParams.get("returnTo"), "/");
  const returningToRapidRecord = returnTo.startsWith("/rapid-record");

  const [isLogin, setIsLogin] = useState(
    () => searchParams.get("mode") !== "signup"
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");

  const resetError = () => setError("");

  const routeUserAfterLogin = useCallback((freshUserData) => {
    navigate(getPostAuthRoute(freshUserData, returnTo), { replace: true });
  }, [navigate, returnTo]);

  useEffect(() => {
    if (loading || !currentUser || !userData) return;

    if (joiningWorkspace) {
      navigate(`/join/${encodeURIComponent(invitationToken)}`, {
        replace: true,
      });
      return;
    }

    routeUserAfterLogin(userData);
  }, [currentUser, invitationToken, joiningWorkspace, loading, navigate, routeUserAfterLogin, userData]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      if (isLogin) {
        await signIn(email.trim(), password);
        const freshUserData = await refreshUserData();
        if (joiningWorkspace) {
          navigate(`/join/${encodeURIComponent(invitationToken)}`, {
            replace: true,
          });
          return;
        }
        routeUserAfterLogin(freshUserData);
        return;
      }

      const userCred = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      await setDoc(
        doc(db, "users", userCred.user.uid),
        {
          uid: userCred.user.uid,
          email: email.trim(),
          fullName: fullName.trim(),
          companyName: joiningWorkspace ? "" : companyName.trim(),
          city: joiningWorkspace ? "" : city.trim(),
          state: joiningWorkspace ? "" : stateCode.trim(),

          accessApproved: false,
          accessStatus: "pending",
          role: "user",
          subscriptionActive: false,
          preferredLanguage: normalizeSBSLanguage(
            i18n.resolvedLanguage || i18n.language
          ),

          createdAt: new Date(),
          updatedAt: new Date(),
        },
        { merge: true }
      );

      navigate(
        joiningWorkspace
          ? `/join/${encodeURIComponent(invitationToken)}`
          : returnTo.startsWith("/pricing") || /^\/client(?:\/|$)/.test(returnTo.split(/[?#]/)[0])
          ? returnTo
          : "/pricing",
        { replace: true }
      );
    } catch (err) {
      console.error("Auth error:", err);
      setError(
        err.message ||
          t("auth.failed", {
            action: isLogin ? t("auth.actions.login") : t("auth.actions.create"),
          })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        returningToRapidRecord ? "auth-page auth-page-rapid" : "auth-page"
      }
    >
      <header className="auth-topbar">
        <a className="auth-brand" href="https://selenne.io">
          <img src="/icons/icon-512x512.png" alt="" width="46" height="46" />
          <span>Selenne<span>Business Suite</span></span>
        </a>
        <div className="auth-topbar-actions">
          <a className="auth-explore" href="https://selenne.io">{t("auth.explore")} <ArrowUpRight size={15} aria-hidden="true" /></a>
          <LanguageSwitcher compact />
        </div>
      </header>
      <div className="auth-shell">
        <section className="auth-brand-panel">
          <p className="auth-kicker">{t("auth.workspaceKicker")}</p>

          <h1>
            {returningToRapidRecord
              ? t("auth.rapidHeadline")
              : t("auth.headline")}
          </h1>

          <p className="auth-lead">
            {returningToRapidRecord
              ? t("auth.rapidLead")
              : t("auth.lead")}
          </p>

          <p className="auth-category">{t("auth.category")}</p>
          <div className="auth-workspace-preview">
            <div className="auth-preview-heading"><span>{t("auth.workspacePreview")}</span><span className="auth-connected"><span />{t("auth.connected")}</span></div>
            <div className="auth-center-grid">
              {[
                { key: "operations", Icon: BriefcaseBusiness },
                { key: "finance", Icon: Landmark },
                { key: "hr", Icon: Users },
              ].map(({ key, Icon }) => (
                <div className={`auth-center auth-center-${key}`} key={key}>
                  <Icon size={22} aria-hidden="true" />
                  <strong>{t(`auth.centers.${key}.title`)}</strong>
                  <span>{t(`auth.centers.${key}.description`)}</span>
                </div>
              ))}
            </div>
            <div className="auth-preview-flow"><Check size={16} aria-hidden="true" /><span>{t("auth.connectedWork")}</span></div>
          </div>
          <div className="auth-feature-list">
            <div><Check size={18} aria-hidden="true" /><span><strong>{t("auth.features.marketingTitle")}</strong><p>{t("auth.features.marketingText")}</p></span></div>
            <div><Check size={18} aria-hidden="true" /><span><strong>{t("auth.features.rapidTitle")}</strong><p>{t("auth.features.rapidText")}</p></span></div>
          </div>
          <p className="auth-plan-note">{t("auth.planNote")}</p>
          <a className="auth-plans-link" href="/pricing">{t("auth.viewPlans")} <ArrowUpRight size={16} aria-hidden="true" /></a>
        </section>

        <section className="auth-card">
          <div className="auth-card-header">
            <p className="auth-kicker"><ShieldCheck size={16} aria-hidden="true" />{t("auth.secureAccess")}</p>
            <h2>
              {isLogin
                ? joiningWorkspace
                  ? t("auth.titles.joinSignIn")
                  : returningToRapidRecord
                  ? t("auth.titles.rapid")
                  : t("auth.titles.welcome")
                : joiningWorkspace
                ? t("auth.titles.teammate")
                : t("auth.titles.create")}
            </h2>
            <p>
              {isLogin
                ? joiningWorkspace
                  ? t("auth.descriptions.joinSignIn")
                  : t("auth.descriptions.signIn")
                : joiningWorkspace
                ? t("auth.descriptions.teammate")
                : t("auth.descriptions.create")}
            </p>
          </div>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <form className="auth-form" onSubmit={handleAuth}>
            {!isLogin && (
              <>
                <label>
                  {t("auth.fields.fullName")}
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => {
                      resetError();
                      setFullName(e.target.value);
                    }}
                    placeholder={t("auth.fields.fullNamePlaceholder")}
                    required
                  />
                </label>

                {!joiningWorkspace && (
                  <>
                    <label>
                      {t("auth.fields.companyName")}
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => {
                          resetError();
                          setCompanyName(e.target.value);
                        }}
                        placeholder={t("auth.fields.companyPlaceholder")}
                        required
                      />
                    </label>

                    <div className="auth-two-col">
                      <label>
                        {t("auth.fields.city")}
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => {
                            resetError();
                            setCity(e.target.value);
                          }}
                          placeholder={t("auth.fields.city")}
                          required
                        />
                      </label>

                      <label>
                        {t("auth.fields.state")}
                        <input
                          type="text"
                          value={stateCode}
                          onChange={(e) => {
                            resetError();
                            setStateCode(e.target.value);
                          }}
                          placeholder={t("auth.fields.state")}
                          required
                        />
                      </label>
                    </div>
                  </>
                )}
              </>
            )}

            <label>
              {t("auth.fields.email")}
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  resetError();
                  setEmail(e.target.value);
                }}
                placeholder="you@example.com"
                required
              />
            </label>

            <label>
              {t("auth.fields.password")}
              <input
                type="password"
                autoComplete={isLogin ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => {
                  resetError();
                  setPassword(e.target.value);
                }}
                placeholder={t("auth.fields.passwordPlaceholder")}
                required
              />
            </label>

            <button type="submit" className="auth-primary-btn" disabled={submitting}>
              {submitting
                ? isLogin
                  ? t("auth.actions.loggingIn")
                  : t("auth.actions.creating")
                : isLogin
                ? t("auth.actions.login")
                : t("auth.actions.create")}
            </button>
          </form>

          <div className="auth-toggle">
            <span>
              {isLogin ? t("auth.actions.needAccount") : t("auth.actions.alreadyAccount")}
            </span>

            <button
              type="button"
              onClick={() => {
                setIsLogin((prev) => !prev);
                setError("");
              }}
            >
              {isLogin
                ? joiningWorkspace
                  ? t("auth.actions.teammate")
                  : t("auth.actions.request")
                : t("auth.actions.login")}
            </button>
          </div>

          <div className="auth-note">
            <strong>{t("auth.clientNoteLabel")}</strong> {t("auth.clientNote")}
          </div>

          <WebAppInstall />

          <div className="auth-legal-links">
            <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
              {t("auth.privacy")}
            </a>
            <span>|</span>
            <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
              {t("auth.terms")}
            </a>
          </div>
        </section>
      </div>

      <footer className="auth-footer">
        &copy; {new Date().getFullYear()} Selenne Business Suite. {t("auth.footer")}{" "}
        <a
          href="https://selennestrategies.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Selenne Strategies, LLC
        </a>
        . {t("auth.rights")}
      </footer>
    </div>
  );
}
